import { DateTime } from 'luxon';

/**
 * The one place the app turns a date into text for a screen (ENG-9640).
 *
 * Four presets, all year first with the month spelled out, so `2026-OCT-08` reads the same to
 * every viewer, and all in 24-hour time. That matches the format ENG-7374 chose for the Studypages
 * workspace. Storage, the API and exports keep ISO; `docs/date-format/PLAN.md` has the sites and
 * `RESEARCH.md` the reasons. The Entry form's DateTime field keeps its own configured format and
 * does not come through here.
 *
 * Every preset shows the viewer's local time. Only `formatAuditTimestamp` names the zone, which the
 * Part 11 screens (audit log, e-signatures) owe the reader.
 *
 * The locale is pinned to English: Luxon's `MMM` follows the system locale otherwise (`Okt.` in a
 * German browser), and the app ships Spanish copy as well. Luxon has no uppercase-month token, so
 * the formatter uppercases the month part on its own.
 */

/**
 * The same date as a PrimeVue DatePicker format, for the pickers outside the Entry form: `yy` is a
 * four-digit year there and `M` a short month, so it reads `2026-Oct-08`. Set per picker, never as
 * PrimeVue's global default, because an Entry DateTime field with no format falls back to that.
 */
export const DATE_PICKER_FORMAT = 'yy-M-dd';

/** What a display site hands in: a payload's ISO string, a model's `Date`, or a Luxon value. */
export type DisplayDate = string | Date | DateTime | null | undefined;

export type DisplayOptions = {
  /**
   * `'utc'` reads the value's UTC wall clock instead of the viewer's. Only for a value that stores
   * a wall-clock time as UTC, such as a case note's event time against a timepoint.
   */
  zone?: 'utc';
};

/**
 * A timestamp string off the wire, read the way moment read it: ISO first, then the SQL style with a
 * space (`2026-10-08 14:05:09`) and RFC 2822, each without an offset in the viewer's zone. Never
 * `new Date`, which reads a date-only `2026-10-08` as UTC midnight, the day before west of UTC.
 * Unreadable text, or a field the payload left out, gives an invalid `DateTime` rather than throwing,
 * which `fromSQL` would do on `undefined`.
 */
export const parseTimestamp = (value: string): DateTime => {
  if (typeof value !== 'string') {
    return DateTime.invalid('not a string');
  }
  const iso = DateTime.fromISO(value);
  if (iso.isValid) {
    return iso;
  }
  const sql = DateTime.fromSQL(value);
  return sql.isValid ? sql : DateTime.fromRFC2822(value);
};

const toDateTime = (value: DisplayDate, options: DisplayOptions = {}): DateTime | undefined => {
  let date: DateTime;
  if (value instanceof DateTime) {
    date = value;
  } else if (value instanceof Date) {
    date = DateTime.fromJSDate(value);
  } else if (typeof value === 'string' && value !== '') {
    date = parseTimestamp(value);
  } else {
    return undefined;
  }
  if (!date.isValid) {
    return undefined;
  }
  return (options.zone ? date.setZone(options.zone) : date).setLocale('en');
};

const yearMonthDay = (date: DateTime): string =>
  `${date.toFormat('yyyy')}-${date.toFormat('MMM').toUpperCase()}-${date.toFormat('dd')}`;

/** `2026-OCT-08`. For values that are a day, not a moment: expiry dates, chart day labels. */
export const formatDate = (value: DisplayDate, options?: DisplayOptions): string => {
  const date = toDateTime(value, options);
  return date ? yearMonthDay(date) : '';
};

/** `2026-OCT-08 14:05`. Every timestamp that is not an audit record. */
export const formatDateTime = (value: DisplayDate, options?: DisplayOptions): string => {
  const date = toDateTime(value, options);
  return date ? `${yearMonthDay(date)} ${date.toFormat('HH:mm')}` : '';
};

/** `14:05`. A time of day with no date. */
export const formatTime = (value: DisplayDate, options?: DisplayOptions): string =>
  toDateTime(value, options)?.toFormat('HH:mm') ?? '';

/**
 * `2026-OCT-08 14:05:09 GMT+5:30`. The audit log, e-signatures and error details: to the second,
 * and naming the zone by its offset, as FDA's 2024 Part 11 Q&A (Q14) asks of a timestamp. Always
 * the offset (`GMT-7`, `GMT+0`), never Luxon's `ZZZZ`, which prints `PDT` or `CST` for US zones:
 * an abbreviation is ambiguous (`CST` is also China's) and two viewers' stamps would not compare.
 */
export const formatAuditTimestamp = (value: DisplayDate): string => {
  const date = toDateTime(value);
  return date ? `${yearMonthDay(date)} ${date.toFormat("HH:mm:ss 'GMT'Z")}` : '';
};

export type RelativeOptions = {
  /** Past this many days the value prints as `formatDateTime`. */
  maxDays: number;
  /** What "ago" is measured from. Defaults to the current time. */
  now?: DateTime;
};

/**
 * `2 hours ago` up to `maxDays` old, then `formatDateTime`. Only for passing status, and a caller
 * should put the absolute `formatDateTime` in a tooltip beside it.
 */
export const formatRelative = (value: DisplayDate, { maxDays, now = DateTime.now() }: RelativeOptions): string => {
  const date = toDateTime(value);
  if (!date) {
    return '';
  }
  if (now.diff(date, 'days').days > maxDays) {
    return formatDateTime(date);
  }
  // A value at or past `now` comes from a clock that runs ahead, and reads `0 seconds ago` rather
  // than `in 30 seconds`: Luxon counts an equal pair as future, so the base sits 1 ms after it.
  const base = date < now ? now : date.plus({ milliseconds: 1 });
  return date.toRelative({ base, locale: 'en' }) ?? '';
};
